import { SingleUnit } from './equipmentTypes';
import { UnitRecord } from './equipmentTypes';

export type EquipmentEditChange = {
    from: string | number | boolean | null;
    to: string | number | boolean | null;
};

export type EquipmentEditHistoryEntry = {
    editedBy: string;
    editedByUserId: string;
    editedAt: string;
    changes: Record<string, EquipmentEditChange>;
};

/** Auto / system fields — do not log on overview save. */
export const EQUIPMENT_EDIT_HISTORY_SKIP_FIELDS = new Set([
    'id',
    'owner',
    '__typename',
    'createdAt',
    'updatedAt',
    'image',
    'category',
    'currentUMCounterValue',
    'currentMachineHours',
    'lastInspectionDate',
    'lastDailyServiceDate',
    'lastServiceHours',
    'lastInspectionUMCounter',
    'lastDailyServiceUMCounter',
    'replacedUMCounterHistory',
    'replacedUMCounterStatus',
    'editHistory',
]);

export const EQUIPMENT_EDIT_HISTORY_FIELD_LABELS: Record<string, string> = {
    name: 'Unit Name',
    description: 'Description',
    categoryId: 'Equipment Category',
    manufacturerName: 'Manufacturer',
    manufactureDate: 'Year Manufactured',
    modelNumber: 'Model Number',
    engineManufacturer: 'Engine Manufacturer',
    engineModelNumber: 'Engine Model',
    aquisitionDate: 'Acquisition Date',
    aquisitionCost: 'Acquisition Cost',
    inputDate: 'Input Date',
    inputByPerson: 'Entered By',
    serialNumber: 'Unit Serial Number',
    engineSerialNumber: 'Engine Serial Number',
    licenseNumber: 'License Number',
    unitOfMeasure: 'UM Description',
    machineUMCounterAtAquisition: 'Beginning Hours',
    machineHoursAtAquisition: 'Hours at Acquisition',
    annualProjectedUsage: 'Annual Projected Utilization',
    utilizationAdjustmentDays: 'Adjustment Days',
    replacedUMCounterValue: 'Replaced Hours Counter',
    putInServiceDate: 'Date Placed in Service',
    removedFromServiceDate: 'Removed From Service Date',
    removedFromServiceReason: 'Removed From Service Reason',
    dispositionDate: 'Disposition Date',
    dispositionReason: 'Disposition Reason',
    equipmentStatus: 'In Service',
    locationId: 'Location',
    dailyServiceLogFormId: 'Daily Service Form',
    assignedTo: 'Assigned To',
    serviceCenterId: 'Service Center',
    axleConfiguration: 'Axle Configuration',
    pmModuleEnrollment: 'PM Module',
    tiresModuleEnrollment: 'Tires Module',
    costTrackingModuleEnrollment: 'Cost Tracking Module',
    downReason: 'Down Reason',
    extraUnitInfo: 'Extra Info',
    fuelType: 'Fuel Type',
};

const MAX_EDIT_HISTORY_ENTRIES = 50;

const normalizeComparable = (value: unknown): string | number | boolean | null => {
    if (value == null || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
    }
    return String(value);
};

export function parseEquipmentEditHistory(historyJson?: string | null): EquipmentEditHistoryEntry[] {
    if (!historyJson) return [];
    try {
        const parsed = JSON.parse(historyJson);
        return Array.isArray(parsed) ? (parsed as EquipmentEditHistoryEntry[]) : [];
    } catch {
        return [];
    }
}

export function buildEquipmentEditChanges(
    previous: UnitRecord,
    next: Partial<SingleUnit> | Record<string, unknown>,
): Record<string, EquipmentEditChange> {
    const changes: Record<string, EquipmentEditChange> = {};

    for (const key of Object.keys(next)) {
        if (EQUIPMENT_EDIT_HISTORY_SKIP_FIELDS.has(key)) continue;
        const from = normalizeComparable(previous[key]);
        const to = normalizeComparable((next as Record<string, unknown>)[key]);
        if (String(from ?? '') === String(to ?? '')) continue;
        changes[key] = { from, to };
    }

    return changes;
}

export function appendEquipmentEditHistory(
    existingHistoryJson: string | null | undefined,
    entry: Omit<EquipmentEditHistoryEntry, 'changes'> & {
        changes: Record<string, EquipmentEditChange>;
    },
): string | null {
    if (Object.keys(entry.changes).length === 0) {
        return existingHistoryJson ?? null;
    }

    const history = parseEquipmentEditHistory(existingHistoryJson);
    history.push({
        editedBy: entry.editedBy,
        editedByUserId: entry.editedByUserId,
        editedAt: entry.editedAt,
        changes: entry.changes,
    });

    const trimmed =
        history.length > MAX_EDIT_HISTORY_ENTRIES
            ? history.slice(history.length - MAX_EDIT_HISTORY_ENTRIES)
            : history;

    return JSON.stringify(trimmed);
}

export function formatEquipmentEditHistoryValue(value: string | number | boolean | null): string {
    if (value == null || value === '') return '(empty)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
}

export function getEquipmentEditHistoryFieldLabel(fieldKey: string): string {
    return EQUIPMENT_EDIT_HISTORY_FIELD_LABELS[fieldKey] || fieldKey;
}
